
import { CheckoutABTest, CheckoutConfig } from '../types';

/**
 * Assigns a variation for an active A/B test based on traffic weights.
 * Persists the assignment in localStorage to ensure consistency for the user.
 */
export const getAssignedVariation = (test: CheckoutABTest): string | null => {
  if (!test.isEnabled || test.variations.length === 0) return null;

  const storageKey = `ab_test_assignment_${test.id}`;
  const existingAssignment = localStorage.getItem(storageKey);

  if (existingAssignment) {
    // Check if the assigned variation still exists
    if (test.variations.some(v => v.id === existingAssignment)) {
      return existingAssignment;
    }
  }

  // Calculate total weight (should be 100, but let's be safe)
  const totalWeight = test.variations.reduce((sum, v) => sum + v.trafficWeight, 0);
  if (totalWeight === 0) return null;

  const random = Math.random() * totalWeight;
  let cumulativeWeight = 0;

  for (const variation of test.variations) {
    cumulativeWeight += variation.trafficWeight;
    if (random <= cumulativeWeight) {
      localStorage.setItem(storageKey, variation.id);
      return variation.id;
    }
  }

  return test.variations[0].id;
};

/**
 * Merges the base config with the overrides from the assigned variations of all active tests.
 */
export const applyABTests = (baseConfig: CheckoutConfig): CheckoutConfig => {
  let finalConfig = { ...baseConfig };

  if (!baseConfig.abTests) return finalConfig;

  baseConfig.abTests.forEach(test => {
    if (test.isEnabled) {
      const assignedVarId = getAssignedVariation(test);
      if (assignedVarId) {
        const variation = test.variations.find(v => v.id === assignedVarId);
        if (variation && variation.config) {
          finalConfig = { ...finalConfig, ...variation.config };
        }
      }
    }
  });

  return finalConfig;
};
